import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import {
  TeammateIconOutlineName,
  UseTeammateIconDirective,
} from '@serious-system';
import {
  dropsBaseWhenHighlighted,
  TeammatePresentation,
  toTeammateIconOutlineName,
} from './teammate-avatar.config';

/**
 * Duplicated from `teammate-avatar.component.ts:41` rather than shared, and
 * the duplication is the cheaper of two costs: sharing it means editing that
 * file, whose zero-line diff across this whole epic is the point of the seam.
 * One string literal, cited, beats reopening 732 lines of GSAP for an import.
 */
const FINE_POINTER_QUERY = '(pointer: fine)';

/**
 * The static half of the SB-3519 visual seam (#1255): a teammate wearing one
 * library glyph instead of the generated color/shape/face character.
 *
 * No GSAP, no ground shadow, no hover animation — KD-7 ruled icon visuals get
 * none ("ça serait cheesy"). That is the point of keeping this out of
 * `TeammateAvatarComponent`'s 732 lines rather than adding a branch to it.
 *
 * ## The six presentations
 *
 * Every value below is measured, not chosen. Source of truth:
 * `.ai/plans/SB-3519_teammate-icon-visuals/SB-3519-figma-parity-findings.md`
 * § "Design Spec block", re-confirmed against the Figma file on 2026-08-28.
 * The node ids are given as well as the path, because the findings file lives
 * in `.ai/plans/`, which the repo treats as a frozen archive — the node id
 * outlives the path.
 *
 * | presentation      | Figma node    | Box   | Radius | Fill                      | Glyph      | Glyph color  |
 * | ----------------- | ------------- | ----- | ------ | ------------------------- | ---------- | ------------- |
 * | `card-header`     | `12053:10312` | 40×40 | —      | the card tint, full-bleed | 32 (80%)   | `darkColor`   |
 * | `builder-banner`  | `26745:193213`| —     | —      | none — the banner's own   | 64 fixed   | `darkColor`   |
 * | `page-header`     | `12053:10788` | 64×64 | 8      | teammate tint @ 16%       | 40 (62.5%) | `darkColor`   |
 * | `nav-slot`        | `12046:9266`  | 48×48 | 8      | ⚠️ teammate tint @ 16%    | 32 (66.7%) | ⚠️ `darkColor` |
 * | `admin-row`       | `12046:7676`  | 48×48 | 8      | ⚠️ teammate tint @ 16%    | 32 (66.7%) | ⚠️ `darkColor` |
 * | `admin-dropdown`  | `12046:7513`  | 32×32 | 4      | ⚠️ teammate tint @ 16%    | 20 (62.5%) | ⚠️ `darkColor` |
 *
 * ⚠️ marks a cell the 2026-08-28 ruling below **demoted**, and every one is now
 * corrected in `TREATMENTS` — `nav-slot` by T6 (#1257), the two admin rows by
 * T7 (#1258). The Figma values they replaced were a fixed `primary-50`
 * container under a fixed `primary-300` glyph. **The ruling demoted color, not
 * geometry** — every Box, Radius and Glyph cell above is still the measured
 * reference, on every row. `builder-banner` was added on 2026-09-01 by a
 * PLACEMENT ruling (T11, #1425) and carries no demoted cell: it is a new
 * surface, not a corrected one.
 *
 * **The boxes are the measured reference, not the rendered size.** Every call
 * site already owns its own box (`size-8`, `size-12`, `size-14
 * tablet-landscape:size-16`, `h-38`, …) and the avatar simply fills it — so
 * this component fills its host too, and expresses each presentation as fill,
 * radius, stroke and a glyph *proportion*. Hard-coding 64×64 would clip on the
 * Teammate page, whose slot is 56 px below `tablet-landscape`.
 *
 * ## ⚠️ The color cells are governed by the spec, not by the table above
 *
 * **Ruled by @maxeber, 2026-08-28: the spec wins, and the Figma file is a
 * prototype — an example, not an authority.** The table was derived from that
 * file, which paints a fixed primary on the three cropped surfaces. The spec's
 * Rule 3 says the glyph is *always* the dark instance of the teammate's color,
 * so those cells are demoted.
 *
 * **The spec's second rule is a reference, and the referent is in this repo.**
 * Story 1: the container "follows the same color treatment as the default
 * teammate visual system". `teammate-avatar.component.ts:196-209` paints the
 * teammate's base color at full opacity under white at 84 % — the same 16 %
 * composite `teammateTint` builds — and DROPS the base layer when `active` or
 * `hovered`, which leaves 84 % white over the parent. That is what
 * `dropsBaseWhenHighlighted` reads, and why this component takes both inputs.
 *
 * **State of the flip: complete.** `nav-slot` landed with T6 (#1257), the two
 * admin rows with T7 (#1258).
 *
 * **Where the highlight comes from, per surface.** `nav-slot` is driven: the
 * row supplies `hovered` and `active`. The two `admin-dropdown` sites supply
 * neither, so this component tracks its own pointer, mirroring the avatar —
 * see `internallyHovered`. Both `admin-row` sites carry `pointer-events-none`,
 * so neither path reaches them, and that is correct: the DEFAULT visual does
 * not invert there either, and the Builder's tile says in a comment that a
 * read-only preview should not.
 *
 * **The `admin-row` hover inversion is not built here, and it is a
 * pre-existing gap rather than an icon-visual decision.** The design does ask
 * for it (`12046:7603` → `12046:7569`: row `Primary/50`, chip `Shades/White`),
 * and it is missing for the DEFAULT visual too — both `admin-row` call sites
 * carry `pointer-events-none`, so no teammate avatar on that surface can see a
 * hover at all. Building it for icons alone would make an icon teammate differ
 * from the default teammate in the same row, which inverts the goal.
 *
 * Closing it properly is a shared-component change, which is why it is not
 * this epic's: the row would have to hover `Primary/50` where
 * `data-table.component.ts:110-112` hovers `neutral-50`, and carry a `group`
 * class for the chip to hook. Spun off rather than absorbed.
 *
 * ## Why the highlight exists, and what merely corroborates it
 *
 * **The authority is the spec, not a node.** Story 1: the container "follows
 * the same color treatment as the default teammate visual system", and that
 * system drops its base layer when highlighted. Verified in a browser: on the
 * nav row, hovered, this arm paints `rgba(255,255,255,0.84)` while the default
 * arm beside it paints its own white layer at `opacity: 0.84` — the two are
 * indistinguishable in that state, which is the property the mixed-list
 * criterion exists to protect.
 *
 * An earlier version of this docblock escalated the highlight as an
 * unreconciled conflict, on the strength of the findings' "Nav item does not
 * vary … byte-identical" bullet. **That bullet is factually wrong**, and the
 * three nodes were re-read on 2026-09-01 to settle what the file contains:
 *
 * | Node          | State    | Row fill     | Chip fill          |
 * | ------------- | -------- | ------------ | ------------------ |
 * | `12046:9100`  | Default  | none         | `primary-faded`    |
 * | `12046:9105`  | Hovered  | `primary-faded` | **`Shades/White`** |
 * | `12046:9110`  | Selected | `primary-faded` | **`Shades/White`** |
 *
 * The same inversion holds on `admin-dropdown` (`12046:7483` → `12046:7488`).
 * The escalation is withdrawn on that basis — the contradiction it rested on
 * does not exist.
 *
 * ⚠️ **Read that table for what it is.** A node id proves what the prototype
 * DRAWS; it never proves what was DECIDED — this file's own header demotes the
 * prototype, and the `card-header` arm below records a ruling (Charles
 * rejected the centred variant) that no node read would reveal. So the table
 * above corrects a false measurement; it is not the reason the highlight is
 * built, and it must not be cited as one.
 *
 * One deliberate delta: the design paints pure `#ffffff`, this component
 * paints 84 % white over the row, which composites to ≈`#fefeff`. That is the
 * DEFAULT visual's mechanism, and matching it is what keeps an icon teammate
 * and a default teammate identical side by side — which is the spec's actual
 * rule. One step out of 255, in favor of the rule over the swatch.
 */
@Component({
  selector: 'squadbox-teammate-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block h-full w-full',
    '(pointerenter)': 'trackInternalHover(true)',
    '(pointerleave)': 'trackInternalHover(false)',
  },
  imports: [UseTeammateIconDirective],
  template: `
    @if (presentation() === 'card-header') {
      <!--
        The Landing card's header. There is no container on the glyph itself —
        it sits bare on the card's tinted header, top-left, at the avatar
        frame's own 24 px padding (Figma 12053:10311 at (24,24); the glyph
        lands at (28,28)). Charles tested a centred version and rejected it:
        "l'autre est plus élégant, plus corpo."

        The tint is the part that would otherwise be lost. On the avatar arm
        TeammateAvatarComponent paints it itself (:97-101), and the findings
        are explicit that it is NOT icon-specific — "only the header's first
        child swaps". So the icon arm reproduces the same layer, gated on the
        same transparentBackground input.

        This presentation used to serve the Builder banner as well, with the
        tint gated off. It no longer does, and the reason is that Charles's
        ruling was read wider than it went: @maxeber scoped it on 2026-09-01 —
        « En coin, c'est pour la teammate landing-page et centré, c'est pour
        le teammate builder ». The corner is this card's; the banner moved to
        builder-banner below (T11, #1425).
      -->
      <div class="relative h-full w-full overflow-hidden">
        @if (!transparentBackground()) {
          <div
            class="absolute inset-0 rounded-t-2xl"
            [style.background-color]="teammateTint()"
          ></div>
        }
        <div
          class="absolute top-6 left-6 flex size-10 items-center justify-center"
        >
          <svg
            class="size-8"
            [style.color]="darkColor()"
            [sdUseTeammateIcon]="resolvedIcon()"
          ></svg>
        </div>
      </div>
    } @else if (presentation() === 'builder-banner') {
      <!--
        The Teammate Builder's banner: the same bare glyph as card-header, on
        the same 'card' avatar variant, in the opposite place. Centred, at
        the measured 64 px — Figma 26745:193213 is a 64×64 instance at y=88
        inside the 240 px banner, and 88 + 32 = 120 is the banner's exact
        middle. The host box is already centred there, so nothing at the call
        site moves.

        No tint layer, and no transparentBackground gate for one. This
        presentation has a single call site and it always passes true, because
        the banner paints its own background (avatarBannerBg()). The wrapper
        still forwards the input to this component, and this branch ignores it:
        on that surface it is the AVATAR arm's alone, which is why the site must
        keep passing it even though nothing below reads it.

        A fixed 64 px rather than a TREATMENTS row, because that table's
        glyphPercent is a share of its CONTAINER and this glyph's host box is
        a non-square 150×170: no single percentage draws a 64 px square in it.
      -->
      <div class="flex h-full w-full items-center justify-center">
        <svg
          class="size-16"
          [style.color]="darkColor()"
          [sdUseTeammateIcon]="resolvedIcon()"
        ></svg>
      </div>
    } @else {
      <div
        class="relative flex h-full w-full items-center justify-center"
        [class]="containerClass()"
        [style.background-color]="containerTint()"
      >
        <svg
          [style.width.%]="glyphPercent()"
          [style.height.%]="glyphPercent()"
          [style.color]="glyphColor()"
          [sdUseTeammateIcon]="resolvedIcon()"
        ></svg>
        @if (presentation() === 'page-header') {
          <!--
            The white ring is an OVERLAY, not a layout border, and that is the
            same idiom TeammateAvatarComponent:279-282 uses for this exact ring
            on this exact surface (variant 'large', which page-header maps onto).
            It has to be: the findings measure 12 + 40 + 12 = 64, so the 4px
            stroke sits ON the box rather than inside it. As a border in the
            flex flow it would shrink the content box to 56px under
            border-box sizing, and 62.5% of that draws a 35px glyph instead of
            the measured 40 — 30px below tablet-landscape, where the slot is 56.
          -->
          <div
            class="pointer-events-none absolute inset-0 rounded-lg border-4 border-shades-white"
          ></div>
        }
      </div>
    }
  `,
})
export class TeammateIconComponent {
  public readonly icon = input.required<TeammateIconOutlineName>();
  public readonly baseColor = input.required<string>();
  public readonly darkColor = input.required<string>();
  public readonly presentation = input.required<TeammatePresentation>();
  /** Honoured by `card-header` only, mirroring the avatar's own input. */
  public readonly transparentBackground = input<boolean>(false);
  /**
   * The highlight pair, with the avatar's own signatures — including
   * `hovered`'s tri-state, so a site can move between the two arms without
   * changing its bindings. See `isHighlighted` for what `undefined` means here.
   */
  public readonly hovered = input<boolean | undefined>(undefined);
  public readonly active = input<boolean>(false);

  /**
   * 84 % white over whatever is behind — the avatar's cropped branch with its
   * base layer dropped (`teammate-avatar.component.ts:196-209`).
   *
   * Translucent on purpose, and this is the one value that is NOT a variant of
   * `teammateTint()`: in the nav the parent is `bg-primary-50` while hovered or
   * active, so an opaque white would paint over it instead of letting it show
   * through at 16 %. Written as the alpha it is rather than as a `color-mix`
   * against `transparent` — that form resolves to the same color, but only
   * through premultiplied interpolation, which reads as a gray to anyone who
   * assumes otherwise.
   */
  private static readonly TINT_HIGHLIGHTED = 'rgb(255 255 255 / 0.84)';

  /**
   * The second membership test, and it is not redundant with the resolver's.
   *
   * `getTeammateVisualFromParts` filters what it builds, but a call site that
   * constructs `TeammateVisual` by hand — a future consumer, a spec fixture, a
   * cast — reaches this input directly. There is no framework backstop
   * underneath either: `BaseIconDirective` writes `xlink:href` with a raw
   * `setAttributeNS`, outside Angular's Renderer and its URL sanitizer. So the
   * guard runs again here, which #1255 carries as an acceptance criterion.
   */
  protected readonly resolvedIcon = computed(() =>
    toTeammateIconOutlineName(this.icon()),
  );

  /**
   * 16 % of the teammate's base color over white — which is exactly what
   * Figma's two stacked fills (`Teammate/A` + `#ffffff` at 84 %) compute.
   * Reused verbatim from `app-loading-screen.component.ts:25` rather than
   * re-derived; note that `admin-teammate-builder-modal.component.ts:841`
   * carries a divergent `${baseColor}22` approximation (~13 %, alpha over
   * parent) which the findings classify as pre-existing drift, not a second
   * sanctioned idiom.
   */
  protected readonly teammateTint = computed(
    () =>
      `color-mix(in srgb, ${this.baseColor()} 16%, var(--color-shades-white) 84%)`,
  );

  /**
   * The centred-container treatments, as one table keyed by presentation.
   *
   * Deliberately a `Record` over the closed union rather than a `switch` with a
   * `default:` arm. A seventh presentation then fails the BUILD instead of
   * silently inheriting someone else's container and glyph size. Two
   * `default:` arms here previously did exactly that, and both were
   * unreachable, which is how they went unnoticed. The sixth —
   * `builder-banner` — is what the guard was written for, and it did fail the
   * build first.
   *
   * `card-header` and `builder-banner` are absent on purpose: neither is a
   * centred container at all. Both are bare glyphs on a surface that paints its
   * own background, differing only in placement, and each lives in its own
   * template branch. `null` records that rather than leaving a hole a reader
   * has to interpret.
   *
   * `glyphPercent` is the glyph's share of its container, straight from the
   * measured Figma pairs — and it is only correct because the page-header ring
   * is an overlay. As a layout border it would shrink the content box and draw
   * 35px where the findings measure 40.
   */
  private static readonly TREATMENTS: Record<
    TeammatePresentation,
    {
      containerClass: string;
      glyphPercent: number;
      teammateTinted: boolean;
    } | null
  > = {
    'card-header': null,
    'builder-banner': null,
    'page-header': {
      containerClass: 'rounded-lg',
      glyphPercent: 62.5, // 40 of 64
      teammateTinted: true,
    },
    'nav-slot': {
      // Teammate-colored since T6 (#1257). The fixed `bg-primary-50
      // text-primary-300` pair that used to sit here was measured off the
      // Figma prototype; the spec's Rule 3 says the glyph is ALWAYS the dark
      // instance of the teammate's color, and the 2026-08-28 ruling settled
      // that the spec wins. Geometry below is untouched by that ruling.
      containerClass: 'rounded-lg',
      glyphPercent: 66.67, // 32 of 48
      teammateTinted: true,
    },
    'admin-row': {
      // Teammate-colored since T7 (#1258), same ruling as `nav-slot`. Both of
      // this presentation's sites carry `pointer-events-none`, so it never
      // reaches the highlighted state — see `internallyHovered`.
      containerClass: 'rounded-lg',
      glyphPercent: 66.67, // 32 of 48
      teammateTinted: true,
    },
    'admin-dropdown': {
      // Teammate-colored since T7 (#1258). Unlike `admin-row`, this one DOES
      // highlight: the two dropdown sites leave pointer events enabled, and
      // the default visual inverts there through its own handlers.
      containerClass: 'rounded',
      glyphPercent: 62.5, // 20 of 32
      teammateTinted: true,
    },
  };

  private readonly treatment = computed(
    () => TeammateIconComponent.TREATMENTS[this.presentation()],
  );

  /**
   * Whether the CENTERED CONTAINER belongs to the teammate-colored family —
   * `page-header` and, since T6 (#1257), `nav-slot`.
   *
   * Not the same question as "is this presentation teammate-colored", which is
   * true of `card-header` too: that one paints the teammate tint in its own
   * template branch, full-bleed behind the glyph, rather than on a container.
   *
   * Read it as a BRANCH GATE, not as a claim about the paint. It is true in
   * the highlighted state as well, where the container carries no teammate
   * color at all — that is the state the family drops into, not a different
   * family. An earlier `usesTeammateTintContainer` asserted the paint, and
   * stopped being true the moment `nav-slot` gained a highlighted state.
   */
  protected readonly isTeammateColoredContainer = computed(
    () => this.treatment()?.teammateTinted ?? false,
  );

  /**
   * The avatar's internal hover, mirrored — not reinvented.
   *
   * `hovered` is tri-state: `undefined` means "no site is driving me, track my
   * own pointer". `TeammateAvatarComponent:433-447` does exactly this, and both
   * admin dropdowns depend on it — neither site supplies the input, and the
   * default visual inverts there when the cursor is over its 32 px chip.
   *
   * Two guards, both copied from the avatar rather than chosen here. A
   * site-driven `hovered` wins outright, including `false`, so the nav row
   * cannot be highlighted by a stray pointer event. And a coarse pointer never
   * highlights: on touch, `pointerenter` fires on tap and never leaves.
   *
   * ⚠️ Not reachable on `admin-row`. Both of its sites disable pointer events
   * (`admin-teammates.page.ts:171`, `admin-teammate-builder-modal.component.ts:531`,
   * the latter with a comment saying the read-only preview wants no
   * base-color-layer toggle), so these handlers cannot fire — which is how the
   * icon arm keeps the same no-highlight behavior the default visual has there.
   */
  private readonly internallyHovered = signal(false);

  protected readonly trackInternalHover = (hovered: boolean): void => {
    if (this.hovered() !== undefined) {
      return;
    }
    if (!window.matchMedia(FINE_POINTER_QUERY).matches) {
      return;
    }
    this.internallyHovered.set(hovered);
  };

  /**
   * Highlighted = hovered or active, on a presentation that drops its base.
   *
   * The `??` is the avatar's own expression (`:197`), and the reason the
   * fallback belongs behind it rather than beside it: a site that drives
   * `hovered` owns the answer completely.
   */
  protected readonly isHighlighted = computed(
    () =>
      dropsBaseWhenHighlighted(this.presentation()) &&
      (this.active() || (this.hovered() ?? this.internallyHovered())),
  );

  protected readonly containerTint = computed(() => {
    if (!this.isTeammateColoredContainer()) {
      return null;
    }
    return this.isHighlighted()
      ? TeammateIconComponent.TINT_HIGHLIGHTED
      : this.teammateTint();
  });

  /**
   * The glyph is the teammate's dark color in EVERY state of the
   * teammate-colored family — the spec's Rule 3 has no hover carve-out, and
   * the default visual likewise keeps its shape in the base color while the
   * background drops away beneath it.
   */
  protected readonly glyphColor = computed(() =>
    this.isTeammateColoredContainer() ? this.darkColor() : null,
  );

  protected readonly containerClass = computed(
    () => this.treatment()?.containerClass ?? '',
  );

  protected readonly glyphPercent = computed(
    () => this.treatment()?.glyphPercent ?? 80,
  );
}
